import { memoize } from 'lodash-es';
import moment, { type Moment } from 'moment';
import momentTz from 'moment-timezone';

import type { SupportedTimeZone } from '@/constants/date';

/**
 * Reserved sentinel stored in a date/dob field to represent a
 * known-to-be-unknown date (distinct from an empty/missing value). Only fields
 * with `allowUnknown` set should accept it. Must never be parsed as a date;
 * renderers display it verbatim as "Unknown". Mirrors nomos `UNKNOWN_DATE_VALUE`.
 */
export const UNKNOWN_DATE_VALUE = 'Unknown';

/** True when a stored value is the reserved unknown-date sentinel. */
export const isUnknownDateValue = (value: unknown): boolean => value === UNKNOWN_DATE_VALUE;

export const TIMEZONE_MAP: Record<SupportedTimeZone, string> = {
  IST: 'Asia/Kolkata',
  ET: 'America/New_York',
  CT: 'America/Chicago',
  MT: 'America/Denver',
  PT: 'America/Los_Angeles',
};

/**
 * Parse a date and convert it to the specified timezone.
 * - ISO strings with 'Z' suffix (UTC) are correctly converted to target timezone
 * - ISO strings with timezone offset are correctly converted
 * - Strings without timezone info are treated as UTC for consistency
 */
const getMomentInTz = (date?: string | Date | Moment, timeZone: SupportedTimeZone = 'ET') => {
  if (!date) return null;

  const tz = TIMEZONE_MAP[timeZone];

  // If already a Moment object, convert to target timezone
  if (moment.isMoment(date)) {
    return date.isValid() ? date.clone().tz(tz) : null;
  }

  // Parse as UTC first, then convert to target timezone
  // This ensures consistent behavior regardless of whether the string has timezone info
  const m = moment.utc(date).tz(tz);
  return m.isValid() ? m : null;
};

/**
 * Format date for display in standard format
 * @param date - Date string or Date object
 * @returns Formatted date string or "N/A" if invalid
 */

export const formatDate = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  const m = getMomentInTz(date, timeZone);
  return m ? m.format('MM/DD/YYYY, HH:mm:ss') : 'N/A';
};

/**
 * Format date for display in short format
 * @param date - Date string or Date object
 * @returns Formatted date string or "N/A" if invalid
 */
export const formatDateShort = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  const m = getMomentInTz(date, timeZone);
  return m ? m.format('MM/DD/YYYY') : 'N/A';
};

/**
 * Format time for display
 * @param date - Date string or Date object
 * @returns Formatted time string or "N/A" if invalid
 */
export const formatTime = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  const m = getMomentInTz(date, timeZone);
  return m ? m.format('HH:mm:ss') : 'N/A';
};

/**
 * Get relative time from now
 * @param date - Date string or Date object
 * @returns Relative time string (e.g., "2 hours ago")
 */
export const getRelativeTime = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  const m = getMomentInTz(date, timeZone);
  return m ? m.fromNow() : 'N/A';
};

/**
 * Format date/time in compact format for UI display (e.g., "Dec 25, 3:45 PM")
 * @param date - Date string or Date object
 * @param timeZone - The timezone to display the time in (defaults to DEFAULT_TIMEZONE)
 * @returns Formatted date string or "-" if invalid
 */
export const formatDateTimeCompact = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '-';

  const ianaTimeZone = TIMEZONE_MAP[timeZone];

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: ianaTimeZone,
  });
};

/**
 * Calculate duration between two dates
 * @param startDate - Start date
 * @param endDate - End date (defaults to now)
 * @returns Duration object with hours and minutes
 */
export const calculateDuration = (
  startDate: string | Date | undefined,
  endDate?: string | Date,
  timeZone: SupportedTimeZone = 'ET',
): { hours: number; minutes: number; formatted: string } => {
  const start = getMomentInTz(startDate, timeZone);

  // When endDate is undefined, use current time in the selected timezone
  // (not local timezone converted to selected timezone)
  const end = endDate ? getMomentInTz(endDate, timeZone) : momentTz.tz(TIMEZONE_MAP[timeZone]);

  if (!start || !end) {
    return { hours: 0, minutes: 0, formatted: '0h 0m' };
  }
  const duration = moment.duration(end.diff(start));
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes() % 60);

  return {
    hours,
    minutes,
    formatted: `${hours}h ${minutes}m`,
  };
};

/**
 * Format a call duration from two ISO date strings to a human-readable string (e.g., "4m 32s").
 * Used by call history tables and cards.
 * @param startedAt - ISO date string for call start
 * @param endedAt - ISO date string for call end
 * @returns Formatted duration string or "—" if inputs are missing/invalid
 */
export function formatCallDuration(startedAt?: string, endedAt?: string): string {
  if (!startedAt || !endedAt) return '—';
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  const diffMs = end - start;
  if (diffMs < 0) return '—';
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/**
 * Format duration in seconds to MM:SS or HH:MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration: (seconds: number) => string = memoize((seconds: number): string => {
  if (
    seconds === null ||
    seconds === undefined ||
    seconds < 0 ||
    !isFinite(seconds) ||
    isNaN(seconds)
  ) {
    return '0:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`;
});

/**
 * Format date with relative labels: Today/Yesterday with time, or full date for older dates
 * @param date - Date string or Date object
 * @returns Formatted string: "Today, HH:mm:ss" | "Yesterday, HH:mm:ss" | "MM/DD/YYYY"
 */

export const formatDateWithRelativeLabels = (
  date: string | Date | undefined,
  timeZone: SupportedTimeZone = 'ET',
): string => {
  if (!date) {
    return 'N/A';
  }

  const targetDate = momentTz.tz(date, TIMEZONE_MAP[timeZone]);

  if (!targetDate.isValid()) {
    return 'N/A';
  }

  const now = momentTz.tz(TIMEZONE_MAP[timeZone]);

  const today = now.clone().startOf('day');
  const yesterday = now.clone().subtract(1, 'day').startOf('day');

  if (targetDate.isSame(today, 'day')) {
    return `Today, ${targetDate.format('HH:mm:ss')}`;
  }

  if (targetDate.isSame(yesterday, 'day')) {
    return `Yesterday, ${targetDate.format('HH:mm:ss')}`;
  }

  return targetDate.format('MM/DD/YYYY, HH:mm:ss');
};

/**
 * Parse a date string formatted by formatDateWithRelativeLabels back to a Date object.
 * This is the reverse operation of formatDateWithRelativeLabels.
 *
 * Supported formats:
 * - "Today, HH:mm:ss"
 * - "Yesterday, HH:mm:ss"
 * - "MM/DD/YYYY, HH:mm:ss"
 * - "MM/DD/YYYY"
 * - ISO 8601 strings
 *
 * @param dateStr - The display date string to parse
 * @param timeZone - The timezone context for Today/Yesterday (defaults to ET)
 * @returns Date object, or undefined if parsing fails
 */
export const parseDateWithRelativeLabels = (
  dateStr: string,
  timeZone: SupportedTimeZone = 'ET',
): Date | undefined => {
  const tz = TIMEZONE_MAP[timeZone];
  const now = momentTz.tz(tz);

  // Handle "Today, HH:mm:ss" format
  if (dateStr.toLowerCase().startsWith('today')) {
    const timeMatch = dateStr.match(/(\d{1,2}):(\d{2}):(\d{2})$/);
    if (timeMatch) {
      const [, hours, minutes, seconds] = timeMatch;
      return now
        .clone()
        .hours(parseInt(hours))
        .minutes(parseInt(minutes))
        .seconds(parseInt(seconds))
        .milliseconds(0)
        .toDate();
    }
  }

  // Handle "Yesterday, HH:mm:ss" format
  if (dateStr.toLowerCase().startsWith('yesterday')) {
    const timeMatch = dateStr.match(/(\d{1,2}):(\d{2}):(\d{2})$/);
    if (timeMatch) {
      const [, hours, minutes, seconds] = timeMatch;
      return now
        .clone()
        .subtract(1, 'day')
        .hours(parseInt(hours))
        .minutes(parseInt(minutes))
        .seconds(parseInt(seconds))
        .milliseconds(0)
        .toDate();
    }
  }

  // Use moment's flexible parsing for other formats
  const m = moment(dateStr, ['MM/DD/YYYY, HH:mm:ss', 'MM/DD/YYYY', moment.ISO_8601]);

  return m.isValid() ? m.toDate() : undefined;
};

/**
 * Format a stored date string to the user-facing MM/DD/YYYY display format.
 * Accepts the canonical yyyy-MM-dd as well as legacy MM-dd-yyyy / MM/dd/yyyy values.
 * @param dateString - Stored date string
 * @returns Formatted date string in MM/DD/YYYY format, or the original string if unrecognized
 */
export const formatDateForDisplay = (dateString: string | undefined | null): string => {
  if (!dateString) {
    return '';
  }

  // Render the unknown-date sentinel verbatim rather than attempting to parse it.
  if (isUnknownDateValue(dateString)) {
    return UNKNOWN_DATE_VALUE;
  }

  const parsed = parseDateFromStorage(dateString);
  if (!parsed) {
    // Return as-is if not a recognized date format
    return String(dateString);
  }

  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = String(parsed.getFullYear()).padStart(4, '0');
  return `${mm}/${dd}/${yyyy}`;
};

/**
 * Validates if a date is correct (no rollover)
 * @param date - The date object to validate
 * @param yearNum - Expected year
 * @param monthNum - Expected month (1-12)
 * @param dayNum - Expected day
 * @returns True if date matches expected values
 */
const isValidDateNoRollover = (
  date: Date,
  yearNum: number,
  monthNum: number,
  dayNum: number,
): boolean => {
  return (
    date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum
  );
};

/**
 * Attempts to parse MM/dd/yyyy or MM-dd-yyyy format date string
 * Supports both slash (primary) and dash (backward compatibility) separators
 * @param valueStr - Date string in MM/dd/yyyy or MM-dd-yyyy format
 * @returns Date object if valid, undefined otherwise
 */
const tryParseMMddyyyy = (valueStr: string): Date | undefined => {
  // Try slash format first (primary format), then dash format (backward compatibility)
  const slashMatch = valueStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const dashMatch = valueStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);

  const match = slashMatch || dashMatch;
  if (!match) {
    return undefined;
  }

  const [, month, day, year] = match;
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  // Validate that parts are numeric
  if (isNaN(monthNum) || isNaN(dayNum) || isNaN(yearNum)) {
    return undefined;
  }

  // Create date (month is 0-indexed in JavaScript)
  const date = new Date(yearNum, monthNum - 1, dayNum);

  // Verify the date is valid (handles invalid dates like 13/32/2025)
  if (isValidDateNoRollover(date, yearNum, monthNum, dayNum)) {
    return date;
  }

  // If format matched but date is invalid, return undefined to fall through
  return undefined;
};

/**
 * Attempts to parse a naive yyyy-MM-dd date string as a LOCAL date.
 * Using an explicit local constructor avoids `new Date("2025-01-15")` being read as
 * UTC midnight, which shifts a calendar day in timezones behind UTC.
 * @param valueStr - Date string in yyyy-MM-dd format
 * @returns Date object if valid, undefined otherwise
 */
const tryParseYyyyMMdd = (valueStr: string): Date | undefined => {
  const match = valueStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return undefined;
  }

  const [, year, month, day] = match;
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) {
    return undefined;
  }

  const date = new Date(yearNum, monthNum - 1, dayNum);
  if (isValidDateNoRollover(date, yearNum, monthNum, dayNum)) {
    return date;
  }

  return undefined;
};

/**
 * Parse date string from storage format to Date object
 * Canonical storage is yyyy-MM-dd; MM/dd/yyyy and MM-dd-yyyy are accepted for backward
 * compatibility with previously stored values.
 * @param value - Date string in yyyy-MM-dd, MM/dd/yyyy, or MM-dd-yyyy format
 * @returns Date object or undefined if value is falsy or invalid
 * @example
 * parseDateFromStorage("2025-01-15") // Returns local Date for January 15, 2025 (canonical)
 * parseDateFromStorage("01/15/2025") // Returns Date object for January 15, 2025 (legacy)
 * parseDateFromStorage("01-15-2025") // Returns Date object for January 15, 2025 (legacy)
 * parseDateFromStorage("2025-01-15T00:00:00Z") // Falls back to standard Date parsing
 * parseDateFromStorage("") // Returns undefined
 */
export const parseDateFromStorage = (value: string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  // The unknown-date sentinel is not a real date — never coerce it into one.
  if (isUnknownDateValue(value)) {
    return undefined;
  }

  const valueStr = String(value);

  // Try canonical yyyy-MM-dd format first (parsed as a local date, no TZ shift)
  const yyyymmddDate = tryParseYyyyMMdd(valueStr);
  if (yyyymmddDate) {
    return yyyymmddDate;
  }

  // Then legacy MM/dd/yyyy and MM-dd-yyyy formats
  const mmddyyyyDate = tryParseMMddyyyy(valueStr);
  if (mmddyyyyDate) {
    return mmddyyyyDate;
  }

  // Fallback to standard Date parsing for other formats (ISO 8601, etc.)
  const fallbackDate = new Date(value);
  return isNaN(fallbackDate.getTime()) ? undefined : fallbackDate;
};

/**
 * Convert a naive date string (YYYY-MM-DD) to a UTC ISO string for API requests,
 * treating the date as being in the specified timezone.
 *
 * @param dateString - Naive date string in YYYY-MM-DD format
 * @param timeZone - The timezone to interpret the date in
 * @param boundary - 'start' for start of day (00:00:00), 'end' for end of day (23:59:59)
 * @returns UTC ISO string for API (e.g., '2026-01-06T05:00:00Z' for ET start of day)
 *
 * @example
 * // User has date "2026-01-06" and timezone "ET"
 * naiveDateToUtc('2026-01-06', 'ET', 'start')
 * // Returns: '2026-01-06T05:00:00Z' (midnight ET = 5am UTC)
 *
 * naiveDateToUtc('2026-01-06', 'ET', 'end')
 * // Returns: '2026-01-07T04:59:59Z' (11:59:59pm ET = next day 4:59:59am UTC)
 */
export const naiveDateToUtc = (
  dateString: string,
  timeZone: SupportedTimeZone = 'ET',
  boundary: 'start' | 'end' = 'start',
): string => {
  const tz = TIMEZONE_MAP[timeZone];

  // Parse the date string as a date in the target timezone
  const m = momentTz.tz(dateString, 'YYYY-MM-DD', tz);

  if (!m.isValid()) {
    // Fallback: return as-is if invalid
    return dateString;
  }

  if (boundary === 'start') {
    m.startOf('day');
  } else {
    m.endOf('day');
  }

  // Convert to UTC and format without milliseconds
  return `${m.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;
};

/**
 * Convert a Date object to a naive date string (YYYY-MM-DD).
 * Used for storing dates in URL params without timezone info.
 *
 * @param date - JavaScript Date object
 * @returns Naive date string in YYYY-MM-DD format
 */
export const dateToNaiveString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Bounds for an API time filter whose start is inclusive and whose end is
 * exclusive (e.g. `occurred_after` / `occurred_before`), from a naive value in
 * `timeZone`. The value is a `YYYY-MM-DDTHH:mm` minute or a whole
 * `YYYY-MM-DD` day; 'end-exclusive' returns the instant just past it, so the
 * chosen minute or day is included in full.
 *
 * @example
 * naiveDateTimeToUtc('2026-01-06T09:30', 'ET', 'start') // '2026-01-06T14:30:00Z'
 * naiveDateTimeToUtc('2026-01-06T17:30', 'ET', 'end-exclusive') // '2026-01-06T22:31:00Z'
 * naiveDateTimeToUtc('2026-01-06', 'ET', 'end-exclusive') // '2026-01-07T05:00:00Z'
 */
export const naiveDateTimeToUtc = (
  value: string,
  timeZone: SupportedTimeZone = 'ET',
  boundary: 'start' | 'end-exclusive' = 'start',
): string => {
  const hasTime = value.includes('T');
  const m = momentTz.tz(
    value,
    hasTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD',
    true,
    TIMEZONE_MAP[timeZone],
  );
  if (!m.isValid()) {
    return value;
  }
  if (boundary === 'end-exclusive') {
    m.add(1, hasTime ? 'minute' : 'day');
  }
  return `${m.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;
};

/**
 * A Date's local calendar day and minute as a naive `YYYY-MM-DDTHH:mm`, the
 * time-carrying counterpart of `dateToNaiveString`.
 */
export const dateToNaiveDateTimeString = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dateToNaiveString(date)}T${hours}:${minutes}`;
};

/**
 * Parse a naive date string (YYYY-MM-DD) to a Date object.
 * Used for initializing date picker from URL params.
 *
 * @param dateString - Naive date string in YYYY-MM-DD format
 * @returns Date object (local time, midnight)
 */
export const naiveStringToDate = (dateString: string): Date | undefined => {
  if (!dateString) return undefined;

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;

  const [, year, month, day] = match;
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

  // Validate the date didn't roll over
  if (
    date.getFullYear() !== parseInt(year, 10) ||
    date.getMonth() !== parseInt(month, 10) - 1 ||
    date.getDate() !== parseInt(day, 10)
  ) {
    return undefined;
  }

  return date;
};

/**
 * Date range preset names supported by the date picker
 */
export type DatePresetName =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | 'last30'
  | 'last60'
  | 'last90';

/**
 * Calculate a date range for a preset in the specified timezone.
 * Returns calendar dates (year, month, day) that represent the correct dates
 * in the target timezone, regardless of browser local time.
 *
 * @param presetName - The preset to calculate (today, yesterday, last7, etc.)
 * @param timeZone - The timezone to use for "today" calculation
 * @returns Object with from and to dates as {year, month, day} objects
 *
 * @example
 * // When it's Jan 13 in IST but Jan 12 in ET
 * getPresetDateRange('today', 'ET')
 * // Returns { from: {year: 2026, month: 0, day: 12}, to: {year: 2026, month: 0, day: 12} }
 */
export const getPresetDateRange = (
  presetName: DatePresetName,
  timeZone: SupportedTimeZone,
): {
  from: { year: number; month: number; day: number };
  to: { year: number; month: number; day: number };
} => {
  const tz = TIMEZONE_MAP[timeZone];
  const now = momentTz.tz(tz);

  let fromMoment = now.clone();
  let toMoment = now.clone();

  switch (presetName) {
    case 'today':
      // fromMoment and toMoment are already "today" in the target timezone
      break;
    case 'yesterday':
      fromMoment = fromMoment.subtract(1, 'day');
      toMoment = toMoment.subtract(1, 'day');
      break;
    case 'last7':
      fromMoment = fromMoment.subtract(6, 'days');
      break;
    case 'last14':
      fromMoment = fromMoment.subtract(13, 'days');
      break;
    case 'last30':
      fromMoment = fromMoment.subtract(29, 'days');
      break;
    case 'last60':
      fromMoment = fromMoment.subtract(59, 'days');
      break;
    case 'last90':
      fromMoment = fromMoment.subtract(89, 'days');
      break;
  }

  return {
    from: { year: fromMoment.year(), month: fromMoment.month(), day: fromMoment.date() },
    to: { year: toMoment.year(), month: toMoment.month(), day: toMoment.date() },
  };
};

/**
 * Pluralize a unit label based on a count (e.g. 1 → "1 min", 2 → "2 mins").
 */
const pluralizeUnit = (count: number, unit: string): string =>
  `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * Format a duration in seconds to a human-readable string for display in timelines.
 * - < 60s  → "within a min"
 * - < 1hr  → "1 min" / "X mins"
 * - >= 1hr → "1 hr" / "X hrs", optionally with " Y min" / " Y mins"
 */
export function formatDurationSeconds(seconds: number | string | undefined | null): string {
  if (seconds === undefined || seconds === null) {
    return '';
  }
  const s = Number(seconds);
  if (isNaN(s) || s < 0) {
    return '';
  }
  if (s < 60) {
    return 'within a min';
  }
  const totalMinutes = Math.floor(s / 60);
  if (totalMinutes < 60) {
    return pluralizeUnit(totalMinutes, 'min');
  }
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const hrLabel = pluralizeUnit(hours, 'hr');
  if (mins === 0) {
    return hrLabel;
  }
  return `${hrLabel} ${pluralizeUnit(mins, 'min')}`;
}

/**
 * Get "today's" date in the specified timezone.
 * Useful for determining what calendar date it currently is in a different timezone.
 *
 * @param timeZone - The timezone to get today's date in
 * @returns Object with year, month (0-indexed), and day
 *
 * @example
 * // When it's Jan 13, 2026 10:00 AM in IST
 * getTodayInTimezone('ET') // Jan 12, 2026 (previous day in ET)
 * // Returns { year: 2026, month: 0, day: 12 }
 */
export const getTodayInTimezone = (
  timeZone: SupportedTimeZone,
): { year: number; month: number; day: number } => {
  const tz = TIMEZONE_MAP[timeZone];
  const now = momentTz.tz(tz);
  return { year: now.year(), month: now.month(), day: now.date() };
};

/**
 * Get a Date object representing today in the specified timezone.
 * The returned Date is a local JS Date but represents the correct calendar day
 * for the given timezone.
 *
 * @param timeZone - The timezone to get today's date in
 * @returns Date object set to midnight of today in the specified timezone
 */
export const getTodayDateInTimezone = (timeZone: SupportedTimeZone): Date => {
  const { year, month, day } = getTodayInTimezone(timeZone);
  return new Date(year, month, day);
};
